import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const VAT_OPTIONS = [
  { value: 23, label: '23%', numeric: 23 },
  { value: 8, label: '8%', numeric: 8 },
  { value: 5, label: '5%', numeric: 5 },
  { value: 0, label: '0%', numeric: 0 },
  { value: -1, label: 'zw. (zwolniony)', numeric: 0 },
  { value: -2, label: 'np. (nie podlega)', numeric: 0 },
];

const UNITS = ['szt.', 'godz.', 'usł.', 'kg', 'm', 'm²', 'm³', 'l', 'kpl.', 'opak.'];
const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP'];
const PAYMENT_METHODS = [
  { value: 'przelew', label: '💳 Przelew bankowy' },
  { value: 'gotowka', label: '💵 Gotówka' },
  { value: 'karta', label: '💳 Karta płatnicza' },
];

const inputStyle = {
  width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 15,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.07)', color: '#fff',
  outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const labelStyle = { display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 6, fontWeight: 700, letterSpacing: '0.02em' };

const today = () => new Date().toISOString().split('T')[0];
const addDays = (d, n) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().split('T')[0];
};

const emptyForm = () => ({
  invoice_type: 'proforma',
  buyer_name: '', buyer_nip: '', buyer_address: '', buyer_email: '',
  issue_date: today(), sale_date: today(),
  due_date: addDays(today(), 14),
  place_of_issue: '',
  payment_method: 'przelew',
  bank_account: '',
  currency: 'PLN',
  notes: '',
  correction_reason: '',
  original_invoice_id: null,
  items: [{ description: '', quantity: 1, unit: 'szt.', unit_price: '', vat_rate: 23 }],
});

export default function OwnerInvoices() {
  const { user, currentCompany, setPage } = useApp();
  const { t } = useI18n();
  const [tab, setTab] = useState('list'); // list | form | incoming
  const [editingId, setEditingId] = useState(null); // null = create, number = edit
  const [invoices, setInvoices] = useState([]);
  const [incomingInvoices, setIncomingInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [nipLoading, setNipLoading] = useState(false);
  const [priceMode, setPriceMode] = useState('netto');
  const [filter, setFilter] = useState('all'); // all|proforma|vat|correction
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ key: '', index: 0 });

  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterContractor, setFilterContractor] = useState('');
  const [filterAmountFrom, setFilterAmountFrom] = useState('');
  const [filterAmountTo, setFilterAmountTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterNumber, setFilterNumber] = useState('');
  const [filterDirection, setFilterDirection] = useState('all'); // all|outgoing|incoming

  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    loadInvoices();
    loadIncoming();
    if (currentCompany?.id) loadBankAccounts();
  }, [currentCompany?.id]);

  async function loadInvoices() {
    setLoading(true);
    try {
      const params = currentCompany?.id ? { company_id: currentCompany.id } : {};
      const { data } = await api.get('/owner/invoices', { params });
      setInvoices(data || []);
    } catch (e) { setInvoices([]); setMsg(e.userMessage || 'Nie udało się pobrać faktur. Spróbuj ponownie.'); } finally { setLoading(false); }
  }

  async function loadIncoming() {
    try {
      const qs = currentCompany?.id ? `?company_id=${currentCompany.id}` : '';
      const { data } = await api.get(`/invoices/in${qs}`);
      setIncomingInvoices(data || []);
    } catch {}
  }

  async function loadBankAccounts() {
    try {
      const { data } = await api.get(`/companies/${currentCompany.id}/bank-accounts`);
      setBankAccounts(data || []);
      if (data?.length > 0 && !form.bank_account) {
        const def = data.find(a => a.is_default) || data[0];
        setForm(f => ({ ...f, bank_account: def.iban, currency: def.currency }));
      }
    } catch {}
  }

  async function lookupNip() {
    const raw = (form.buyer_nip || '').trim();
    const clean = raw.replace(/[^0-9]/g, '');
    const isEU = /^[A-Z]{2}[A-Z0-9]+$/.test(raw.toUpperCase());
    if (!isEU && clean.length !== 10) { setMsg('❌ Nieprawidłowy NIP'); return; }
    setNipLoading(true);
    try {
      if (isEU) {
        const res = await api.get(`/vat/lookup?vat=${raw.toUpperCase()}`);
        if (res.data?.valid) {
          setForm(f => ({ ...f, buyer_name: res.data.name || f.buyer_name, buyer_address: res.data.address || f.buyer_address, buyer_nip: raw.toUpperCase() }));
          setMsg('✅ Dane pobrane z VIES'); setNipLoading(false); return;
        }
      }
      const res = await api.get(`/krs/nip/${clean}`);
      if (res.data?.name) {
        setForm(f => ({ ...f, buyer_name: res.data.name, buyer_address: res.data.address || f.buyer_address, buyer_nip: clean }));
        setMsg('✅ Dane pobrane z bazy'); setNipLoading(false); return;
      }
    } catch {}
    try {
      const gus = await fetch(`https://wl-api.mf.gov.pl/api/search/nip/${clean}?date=${today()}`);
      const gusData = await gus.json();
      if (gusData.result?.subject) {
        const sub = gusData.result.subject;
        setForm(f => ({ ...f, buyer_name: sub.name, buyer_address: sub.workingAddress || sub.residenceAddress || f.buyer_address, buyer_nip: clean }));
        setMsg('✅ Dane pobrane z GUS');
      }
    } catch {}
    setNipLoading(false);
  }

  function upd(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function updItem(i, k, v) { setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; }); }
  function addItem() { setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, unit: 'szt.', unit_price: '', vat_rate: 23 }] })); }
  function removeItem(i) { if (form.items.length <= 1) return; setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); }

  const calculations = useMemo(() => {
    const itemCalcs = form.items.map(it => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unit_price) || 0;
      const vatRateRaw = Number(it.vat_rate);
      const vatRate = vatRateRaw < 0 ? 0 : vatRateRaw;
      let netto, vat, brutto;
      if (priceMode === 'brutto') {
        brutto = qty * price;
        netto = vatRate > 0 ? brutto / (1 + vatRate / 100) : brutto;
        vat = brutto - netto;
      } else {
        netto = qty * price;
        vat = netto * vatRate / 100;
        brutto = netto + vat;
      }
      const unitNetto = priceMode === 'brutto' ? (vatRate > 0 ? price / (1 + vatRate / 100) : price) : price;
      return { netto, vat, brutto, unitNetto, vatRate, vatRateRaw };
    });
    const totals = itemCalcs.reduce((acc, c) => ({
      netto: acc.netto + c.netto, vat: acc.vat + c.vat, brutto: acc.brutto + c.brutto,
    }), { netto: 0, vat: 0, brutto: 0 });
    const vatSummary = {};
    itemCalcs.forEach(c => {
      const key = c.vatRateRaw < 0 ? (c.vatRateRaw === -1 ? 'zw.' : 'np.') : `${c.vatRate}%`;
      if (!vatSummary[key]) vatSummary[key] = { netto: 0, vat: 0, brutto: 0 };
      vatSummary[key].netto += c.netto;
      vatSummary[key].vat += c.vat;
      vatSummary[key].brutto += c.brutto;
    });
    return { itemCalcs, totals, vatSummary };
  }, [form.items, priceMode]);

  // Open create form
  function openCreate() {
    setEditingId(null);
    const f = emptyForm();
    // Pre-fill bank account
    if (bankAccounts.length > 0) {
      const def = bankAccounts.find(a => a.is_default) || bankAccounts[0];
      f.bank_account = def.iban;
      f.currency = def.currency;
    }
    setForm(f);
    setMsg('');
    setTab('form');
  }

  // Open edit form
  function openEdit(inv) {
    setEditingId(inv.id);
    setForm({
      invoice_type: inv.invoice_type || 'proforma',
      buyer_name: inv.buyer_name || '',
      buyer_nip: inv.buyer_nip || '',
      buyer_address: inv.buyer_address || '',
      buyer_email: inv.buyer_email || '',
      issue_date: inv.issue_date || today(),
      sale_date: inv.sale_date || inv.issue_date || today(),
      due_date: inv.due_date || '',
      place_of_issue: inv.place_of_issue || '',
      payment_method: inv.payment_method || 'przelew',
      bank_account: inv.bank_account || '',
      currency: inv.currency || 'PLN',
      notes: inv.notes || '',
      correction_reason: inv.correction_reason || '',
      original_invoice_id: inv.original_invoice_id || null,
      items: (inv.items && inv.items.length > 0)
        ? inv.items.map(it => ({
            description: it.description || '',
            quantity: it.quantity || 1,
            unit: it.unit || 'szt.',
            unit_price: it.unit_price || 0,
            vat_rate: it.vat_rate || 23,
          }))
        : [{ description: '', quantity: 1, unit: 'szt.', unit_price: '', vat_rate: 23 }],
    });
    setMsg('');
    setTab('form');
  }

  // Create correction based on existing invoice
  function openCorrection(inv) {
    setEditingId(null);
    setForm({
      invoice_type: 'correction',
      buyer_name: inv.buyer_name || '',
      buyer_nip: inv.buyer_nip || '',
      buyer_address: inv.buyer_address || '',
      buyer_email: inv.buyer_email || '',
      issue_date: today(),
      sale_date: inv.sale_date || inv.issue_date || today(),
      due_date: '',
      place_of_issue: inv.place_of_issue || '',
      payment_method: inv.payment_method || 'przelew',
      bank_account: inv.bank_account || '',
      currency: inv.currency || 'PLN',
      notes: '',
      correction_reason: '',
      original_invoice_id: inv.id,
      items: (inv.items && inv.items.length > 0)
        ? inv.items.map(it => ({
            description: it.description || '',
            quantity: it.quantity || 1,
            unit: it.unit || 'szt.',
            unit_price: it.unit_price || 0,
            vat_rate: it.vat_rate || 23,
          }))
        : [{ description: '', quantity: 1, unit: 'szt.', unit_price: '', vat_rate: 23 }],
    });
    setMsg(`📝 Korekta do faktury ${inv.invoice_number}`);
    setTab('form');
  }

  async function saveInvoice() {
    if (!form.buyer_name) { setMsg('❌ Wpisz nazwę nabywcy'); return; }
    if (!form.items.some(it => it.description && it.unit_price)) { setMsg('❌ Dodaj przynajmniej jedną pozycję'); return; }

    setSaving(true); setMsg('');
    try {
      const preparedItems = form.items.filter(it => it.description).map((it, i) => {
        const c = calculations.itemCalcs[i];
        return {
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit: it.unit,
          unit_price: Math.round(c.unitNetto * 100) / 100,
          vat_rate: c.vatRate,
          vat_category: c.vatRateRaw === -1 ? 'exempt' : (c.vatRateRaw === -2 ? 'out_of_scope' : null),
        };
      });

      const payload = {
        company_id: currentCompany?.id,
        invoice_type: form.invoice_type,
        buyer_name: form.buyer_name,
        buyer_nip: form.buyer_nip || null,
        buyer_address: form.buyer_address || null,
        buyer_email: form.buyer_email || null,
        issue_date: form.issue_date,
        sale_date: form.sale_date || form.issue_date,
        due_date: form.due_date || null,
        place_of_issue: form.place_of_issue || null,
        payment_method: form.payment_method,
        bank_account: form.bank_account || null,
        currency: form.currency,
        notes: form.notes || null,
        items: preparedItems,
        correction_reason: form.invoice_type === 'correction' ? form.correction_reason : null,
        original_invoice_id: form.original_invoice_id || null,
      };

      if (editingId) {
        await api.put(`/owner/invoice/${editingId}`, payload);
        setMsg('✅ Dokument zaktualizowany!');
      } else {
        await api.post('/owner/invoice/create', payload);
        const typeLabels = { proforma: 'Proforma', vat: 'Faktura VAT', correction: 'Faktura korygująca' };
        setMsg(`✅ ${typeLabels[form.invoice_type]} utworzona!`);
      }
      setTab('list');
      loadInvoices();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  async function deleteInvoice(inv) {
    if (!confirm(`Usunąć ${inv.invoice_number}?`)) return;
    try {
      await api.delete(`/owner/invoice/${inv.id}`);
      setMsg('✅ Dokument usunięty');
      loadInvoices();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
  }

  async function downloadPdf(inv) {
    try {
      const response = await api.get(`/owner/invoice/${inv.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      try {
        const file = new File([blob], `${inv.invoice_number.replace(/\//g, '_')}.pdf`, { type: 'application/pdf' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: inv.invoice_number });
          return;
        }
      } catch {}
      const link = document.createElement('a');
      link.href = url;
      link.download = `${inv.invoice_number.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
  }

  const typeLabels = { proforma: '📋 Proforma', vat: '🧾 Faktura VAT', correction: '📝 Korekta' };
  const typeColors = { proforma: '#f59e0b', vat: '#10b981', correction: '#6366f1' };
  const statusLabels = {
    proforma: 'Proforma', issued: 'Wystawiona', draft: 'Szkic', paid: 'Opłacona',
    cancelled: 'Anulowana', proforma_paid: 'Opłacona', pending_verification: 'Weryfikacja',
  };
  const statusColors = {
    proforma: '#f59e0b', issued: '#10b981', draft: '#6b7280', paid: '#22c55e',
    cancelled: '#ef4444', proforma_paid: '#22c55e', pending_verification: '#3b82f6',
  };

  // Filter + search (advanced)
  const filtered = useMemo(() => {
    let list = invoices;
    if (filter !== 'all') {
      list = list.filter(inv => inv.invoice_type === filter);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(s) ||
        (inv.buyer_name || '').toLowerCase().includes(s)
      );
    }
    // Advanced filters
    if (filterDateFrom) {
      list = list.filter(inv => (inv.issue_date || '') >= filterDateFrom);
    }
    if (filterDateTo) {
      list = list.filter(inv => (inv.issue_date || '') <= filterDateTo);
    }
    if (filterContractor.trim()) {
      const c = filterContractor.toLowerCase();
      list = list.filter(inv => (inv.buyer_name || '').toLowerCase().includes(c));
    }
    if (filterAmountFrom) {
      list = list.filter(inv => Number(inv.gross_total || 0) >= Number(filterAmountFrom));
    }
    if (filterAmountTo) {
      list = list.filter(inv => Number(inv.gross_total || 0) <= Number(filterAmountTo));
    }
    if (filterStatus !== 'all') {
      list = list.filter(inv => inv.status === filterStatus);
    }
    if (filterNumber.trim()) {
      const n = filterNumber.toLowerCase();
      list = list.filter(inv => (inv.invoice_number || '').toLowerCase().includes(n));
    }
    return list;
  }, [invoices, filter, search, filterDateFrom, filterDateTo, filterContractor, filterAmountFrom, filterAmountTo, filterStatus, filterNumber]);

  // Filtered incoming
  const filteredIncoming = useMemo(() => {
    let list = incomingInvoices;
    if (filterDateFrom) {
      list = list.filter(inv => (inv.issue_date || inv.created_at || '') >= filterDateFrom);
    }
    if (filterDateTo) {
      list = list.filter(inv => (inv.issue_date || inv.created_at || '') <= filterDateTo);
    }
    if (filterContractor.trim()) {
      const c = filterContractor.toLowerCase();
      list = list.filter(inv => (inv.seller_name || inv.description || '').toLowerCase().includes(c));
    }
    if (filterAmountFrom) {
      list = list.filter(inv => Number(inv.gross_amount || 0) >= Number(filterAmountFrom));
    }
    if (filterAmountTo) {
      list = list.filter(inv => Number(inv.gross_amount || 0) <= Number(filterAmountTo));
    }
    if (filterNumber.trim()) {
      const n = filterNumber.toLowerCase();
      list = list.filter(inv => (inv.invoice_number || inv.file_name || '').toLowerCase().includes(n));
    }
    return list;
  }, [incomingInvoices, filterDateFrom, filterDateTo, filterContractor, filterAmountFrom, filterAmountTo, filterNumber]);

  // ═══════════════════════════════════════════════════
  // TAB: LIST
  // ═══════════════════════════════════════════════════
  const activeListTab = (tab === 'list' || tab === 'incoming') ? tab : null;
  const paginationKey = JSON.stringify([tab, currentCompany?.id, filter, search, filterDateFrom, filterDateTo, filterContractor, filterAmountFrom, filterAmountTo, filterStatus, filterNumber]);
  const pageIndex = pagination.key === paginationKey ? pagination.index : 0;
  const pageSize = 50;
  const rowCount = tab === 'incoming' ? filteredIncoming.length : filtered.length;
  const paginationControls = <div className="invoice-pagination" aria-label="Strony rejestru">
    <button disabled={pageIndex === 0} onClick={() => setPagination({ key: paginationKey, index: pageIndex - 1 })}>← Poprzednia</button>
    <span role="status">{rowCount ? `${pageIndex * pageSize + 1}–${Math.min((pageIndex + 1) * pageSize, rowCount)}` : '0'} z {rowCount}</span>
    <button disabled={(pageIndex + 1) * pageSize >= rowCount} onClick={() => setPagination({ key: paginationKey, index: pageIndex + 1 })}>Następna →</button>
  </div>;

  if (activeListTab) return (
    <div className="page">
      <BackButton />
      <h1>📄 Faktury i dokumenty</h1>
      {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

      <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={openCreate}>
        ➕ Nowy dokument
      </button>

      {/* Outgoing / Incoming tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[
          { v: 'list', label: '📤 Wychodzące', count: invoices.length },
          { v: 'incoming', label: '📥 Przychodzące', count: incomingInvoices.length },
        ].map(t => (
          <button key={t.v} onClick={() => setTab(t.v)} style={{
            flex: 1, padding: '10px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
            background: tab === t.v ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)',
            color: tab === t.v ? '#60a5fa' : 'rgba(255,255,255,0.5)',
            borderBottom: tab === t.v ? '3px solid #3b82f6' : '3px solid transparent',
          }}>
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Type filters (outgoing only) */}
      {tab === 'list' && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {[
            { v: 'all', label: 'Wszystkie' },
            { v: 'proforma', label: '📋 Proformy' },
            { v: 'vat', label: '🧾 Faktura VAT' },
            { v: 'correction', label: '📝 Korekty' },
          ].map(f => (
            <button key={f.v} onClick={() => setFilter(f.v)} style={{
              padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12,
              background: filter === f.v ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)',
              color: filter === f.v ? '#60a5fa' : 'rgba(255,255,255,0.6)',
              fontWeight: filter === f.v ? 700 : 500,
            }}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      <input
        aria-label="Szukaj faktur"
        style={{ ...inputStyle, marginBottom: 8, padding: '10px 14px', fontSize: 13 }}
        placeholder="🔍 Szukaj po numerze lub kliencie..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {/* Advanced filters toggle */}
      <button onClick={() => setShowFilters(p => !p)} style={{
        width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
        background: showFilters ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.04)',
        color: showFilters ? '#818cf8' : 'rgba(255,255,255,0.6)',
        cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: showFilters ? 10 : 12,
        textAlign: 'left',
      }}>
        {showFilters ? '🔍 Filtry ▲' : '🔍 Filtry ▼'}
      </button>

      {showFilters && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, marginBottom: 12,
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          {/* Date range */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>📅 Od</label>
              <input type="date" style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
                value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>📅 Do</label>
              <input type="date" style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
                value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
            </div>
          </div>
          {/* Contractor */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>🏢 Kontrahent</label>
            <input style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
              placeholder="Nazwa kontrahenta..."
              value={filterContractor} onChange={e => setFilterContractor(e.target.value)} />
          </div>
          {/* Amount range */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>💰 Kwota od</label>
              <input type="number" style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
                placeholder="0.00" value={filterAmountFrom} onChange={e => setFilterAmountFrom(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>💰 Kwota do</label>
              <input type="number" style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
                placeholder="999999" value={filterAmountTo} onChange={e => setFilterAmountTo(e.target.value)} />
            </div>
          </div>
          {/* Status */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>📋 Status</label>
            <select style={{ ...inputStyle, padding: '8px 10px', fontSize: 12, appearance: 'auto' }}
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">Wszystkie</option>
              <option value="proforma">Proforma</option>
              <option value="issued">Wystawiona</option>
              <option value="paid">Opłacona</option>
              <option value="draft">Szkic</option>
            </select>
          </div>
          {/* Invoice number */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 4 }}>🔢 Numer faktury</label>
            <input style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
              placeholder="FV/2025/..."
              value={filterNumber} onChange={e => setFilterNumber(e.target.value)} />
          </div>
          {/* Reset */}
          <button onClick={() => {
            setFilterDateFrom(''); setFilterDateTo(''); setFilterContractor('');
            setFilterAmountFrom(''); setFilterAmountTo(''); setFilterStatus('all'); setFilterNumber('');
          }} style={{
            width: '100%', padding: 8, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(239,68,68,0.1)', color: '#f87171', cursor: 'pointer', fontSize: 12,
          }}>
            🗑 Wyczyść filtry
          </button>
        </div>
      )}

      {/* OUTGOING INVOICES LIST */}
      {paginationControls}
      {tab === 'list' && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, opacity: 0.5 }}>Ładowanie...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, background: 'rgba(255,255,255,0.04)', borderRadius: 12 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
              <p style={{ opacity: 0.6 }}>{invoices.length === 0 ? 'Brak dokumentów. Utwórz pierwszy!' : 'Brak wyników dla filtrów'}</p>
            </div>
          ) : (
            <div>
              {filtered.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize).map(inv => {
                const canEdit = ['proforma', 'draft', 'issued'].includes(inv.status);
                const canDelete = ['proforma', 'draft'].includes(inv.status);
                return (
                  <div key={inv.id} className="invoice-register-row" data-invoice-id={inv.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{inv.invoice_number}</div>
                        <div style={{ fontSize: 12, opacity: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.buyer_name}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, color: '#10b981' }}>
                          {Number(inv.gross_total || 0).toFixed(2)} {inv.currency}
                        </div>
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 4,
                          background: statusColors[inv.status] || '#666', color: '#fff',
                        }}>
                          {statusLabels[inv.status] || inv.status}
                        </span>
                      </div>
                    </div>
                    <div className="invoice-amounts">
                      <div><small>Netto · {inv.currency}</small><strong>{Number(inv.net_total ?? 0).toFixed(2)}</strong></div>
                      <div><small>VAT · {inv.currency}</small><strong>{Number(inv.vat_total ?? 0).toFixed(2)}</strong></div>
                      <div><small>Brutto · {inv.currency}</small><strong>{Number(inv.gross_total ?? 0).toFixed(2)}</strong></div>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.5, marginBottom: 8 }}>
                      {inv.issue_date} • Termin: {inv.due_date || '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {canEdit && (
                        <button onClick={() => openEdit(inv)} style={{
                          background: 'rgba(59,130,246,0.25)', border: 'none', color: '#60a5fa',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}>📝 Edytuj</button>
                      )}
                      <button onClick={() => downloadPdf(inv)} style={{
                        background: 'rgba(16,185,129,0.25)', border: 'none', color: '#34d399',
                        borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}>📄 PDF</button>
                      {inv.invoice_type === 'vat' && inv.status === 'issued' && (
                        <button onClick={() => openCorrection(inv)} style={{
                          background: 'rgba(99,102,241,0.25)', border: 'none', color: '#818cf8',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}>📝 Korekta</button>
                      )}
                      {canDelete && (
                        <button onClick={() => deleteInvoice(inv)} style={{
                          background: 'rgba(239,68,68,0.2)', border: 'none', color: '#f87171',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}>🗑 Usuń</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* INCOMING INVOICES LIST */}
      {tab === 'incoming' && (
        <>
          {filteredIncoming.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, background: 'rgba(255,255,255,0.04)', borderRadius: 12 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📥</div>
              <p style={{ opacity: 0.6 }}>Brak faktur przychodzących</p>
            </div>
          ) : (
            <div>
              {filteredIncoming.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize).map(inv => {
                const inStatusColors = { pending: '#f59e0b', approved: '#10b981', rejected: '#ef4444', processing: '#3b82f6' };
                const inStatusLabels = { pending: 'Oczekuje', approved: 'Zatwierdzona', rejected: 'Odrzucona', processing: 'W trakcie' };
                return (
                  <div key={inv.id} style={{
                    background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, marginBottom: 8,
                    borderLeft: '3px solid #3b82f6',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{inv.invoice_number || inv.file_name || `#${inv.id}`}</div>
                        <div style={{ fontSize: 12, opacity: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {inv.seller_name || inv.description || '—'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, color: '#60a5fa' }}>
                          {inv.gross_amount ? `${Number(inv.gross_amount).toFixed(2)} PLN` : '—'}
                        </div>
                        <span style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 4,
                          background: inStatusColors[inv.status] || '#666', color: '#fff',
                        }}>
                          {inStatusLabels[inv.status] || inv.status || 'pending'}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.5 }}>
                      {inv.issue_date || (inv.created_at ? inv.created_at.split('T')[0] : '—')}
                      {inv.category ? ` • ${inv.category}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════
  // TAB: FORM (create / edit)
  // ═══════════════════════════════════════════════════
  return (
    <div className="page">
      <BackButton onClick={() => { setTab('list'); setMsg(''); }} />
      <h1>{editingId ? '📝 Edycja dokumentu' : '📄 Nowy dokument'}</h1>
      {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Document Type */}
      {!editingId && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { v: 'proforma', icon: '📋', label: 'Proforma' },
            { v: 'vat', icon: '🧾', label: 'Faktura VAT' },
            { v: 'correction', icon: '📝', label: 'Korekta' },
          ].map(opt => (
            <button key={opt.v} onClick={() => upd('invoice_type', opt.v)} style={{
              flex: 1, padding: '12px 8px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: form.invoice_type === opt.v ? (typeColors[opt.v] + '33') : 'rgba(255,255,255,0.05)',
              color: form.invoice_type === opt.v ? typeColors[opt.v] : 'rgba(255,255,255,0.6)',
              fontWeight: form.invoice_type === opt.v ? 800 : 500,
              borderBottom: form.invoice_type === opt.v ? `3px solid ${typeColors[opt.v]}` : '3px solid transparent',
              fontSize: 14, transition: 'all 0.2s',
            }}>
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Buyer */}
      <Section title="👤 Nabywca">
        <label style={labelStyle}>NIP nabywcy</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input style={{ ...inputStyle, flex: 1 }} value={form.buyer_nip}
            onChange={e => upd('buyer_nip', e.target.value)} placeholder="0000000000 lub EU VAT" />
          <button className="btn btn-small" onClick={lookupNip}
            disabled={nipLoading} style={{ whiteSpace: 'nowrap', minWidth: 80, borderRadius: 12, fontWeight: 700 }}>
            {nipLoading ? '⏳' : '🔍 Szukaj'}
          </button>
        </div>
        <label style={labelStyle}>Nazwa nabywcy *</label>
        <input style={{ ...inputStyle, marginBottom: 14 }} value={form.buyer_name}
          onChange={e => upd('buyer_name', e.target.value)} placeholder="Nazwa firmy lub imię i nazwisko" />
        <label style={labelStyle}>Adres nabywcy</label>
        <input style={{ ...inputStyle, marginBottom: 14 }} value={form.buyer_address}
          onChange={e => upd('buyer_address', e.target.value)} placeholder="ul. ..., 00-000 Miasto" />
        <label style={labelStyle}>Email nabywcy</label>
        <input style={{ ...inputStyle }} type="email" value={form.buyer_email}
          onChange={e => upd('buyer_email', e.target.value)} placeholder="email@example.com" />
      </Section>

      {/* Dates */}
      <Section title="📅 Daty">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Data wystawienia</label>
            <input style={inputStyle} type="date" value={form.issue_date} onChange={e => upd('issue_date', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Data sprzedaży</label>
            <input style={inputStyle} type="date" value={form.sale_date} onChange={e => upd('sale_date', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Termin płatności</label>
            <input style={inputStyle} type="date" value={form.due_date} onChange={e => upd('due_date', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Miejsce wystawienia</label>
            <input style={inputStyle} value={form.place_of_issue} onChange={e => upd('place_of_issue', e.target.value)} placeholder="Warszawa" />
          </div>
        </div>
      </Section>

      {/* Payment */}
      <Section title="💳 Płatność">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={labelStyle}>Sposób płatności</label>
            <select style={selectStyle} value={form.payment_method} onChange={e => upd('payment_method', e.target.value)}>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Waluta</label>
            <select style={selectStyle} value={form.currency} onChange={e => {
              upd('currency', e.target.value);
              const acc = bankAccounts.find(a => a.currency === e.target.value);
              if (acc) upd('bank_account', acc.iban);
            }}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <label style={{ ...labelStyle, marginTop: 8 }}>Numer konta bankowego</label>
        <select style={selectStyle} value={form.bank_account} onChange={e => upd('bank_account', e.target.value)}>
          <option value="">— Wybierz konto —</option>
          {bankAccounts.map(a => (
            <option key={a.id} value={a.iban}>{a.currency} • {a.iban}{a.bank_name ? ` (${a.bank_name})` : ''}</option>
          ))}
        </select>
      </Section>

      {/* Items */}
      <Section title="📦 Pozycje">
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <button onClick={() => setPriceMode('netto')} style={{
            flex: 1, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer',
            background: priceMode === 'netto' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.04)',
            color: priceMode === 'netto' ? '#10b981' : 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: 13,
          }}>Ceny NETTO</button>
          <button onClick={() => setPriceMode('brutto')} style={{
            flex: 1, padding: 8, borderRadius: 8, border: 'none', cursor: 'pointer',
            background: priceMode === 'brutto' ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.04)',
            color: priceMode === 'brutto' ? '#f59e0b' : 'rgba(255,255,255,0.6)', fontWeight: 600, fontSize: 13,
          }}>Ceny BRUTTO</button>
        </div>

        {form.items.map((it, i) => {
          const c = calculations.itemCalcs[i];
          return (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.05)', borderRadius: 14, padding: 16, marginBottom: 12,
              border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, opacity: 0.5 }}>Pozycja {i + 1}</span>
                {form.items.length > 1 && (
                  <button onClick={() => removeItem(i)} style={{
                    background: 'rgba(239,68,68,0.2)', border: 'none', color: '#f87171',
                    borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 12,
                  }}>🗑</button>
                )}
              </div>
              <textarea style={{ ...inputStyle, marginBottom: 8, minHeight: 44, resize: 'vertical' }} value={it.description}
                onChange={e => updItem(i, 'description', e.target.value)}
                placeholder="Nazwa towaru lub usługi *" rows={2} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.5fr', gap: 10 }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Ilość</label>
                  <input style={inputStyle} type="number" step="0.01" value={it.quantity}
                    onChange={e => updItem(i, 'quantity', e.target.value)} />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Jednostka</label>
                  <select style={selectStyle} value={it.unit} onChange={e => updItem(i, 'unit', e.target.value)}>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Cena {priceMode}</label>
                  <input style={inputStyle} type="number" step="0.01" value={it.unit_price}
                    onChange={e => updItem(i, 'unit_price', e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 11 }}>Stawka VAT</label>
                  <select style={selectStyle} value={it.vat_rate}
                    onChange={e => updItem(i, 'vat_rate', Number(e.target.value))}>
                    {VAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              {c && it.unit_price && (
                <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, opacity: 0.7 }}>
                  <span>Netto: <b>{c.netto.toFixed(2)}</b></span>
                  <span>VAT: <b>{c.vat.toFixed(2)}</b></span>
                  <span>Brutto: <b>{c.brutto.toFixed(2)}</b></span>
                </div>
              )}
            </div>
          );
        })}
        <button onClick={addItem} style={{
          width: '100%', padding: 10, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.2)',
          background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 13,
        }}>
          ➕ Dodaj pozycję
        </button>
      </Section>

      {/* VAT Summary */}
      {Object.keys(calculations.vatSummary).length > 0 && (
        <Section title="📊 Podsumowanie VAT">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                  <th style={{ padding: 6, textAlign: 'left', opacity: 0.7 }}>Stawka</th>
                  <th style={{ padding: 6, textAlign: 'right', opacity: 0.7 }}>Netto</th>
                  <th style={{ padding: 6, textAlign: 'right', opacity: 0.7 }}>VAT</th>
                  <th style={{ padding: 6, textAlign: 'right', opacity: 0.7 }}>Brutto</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(calculations.vatSummary).map(([rate, vals]) => (
                  <tr key={rate} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ padding: 6 }}>{rate}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{vals.netto.toFixed(2)}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{vals.vat.toFixed(2)}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{vals.brutto.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Totals */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.06))',
        borderRadius: 16, padding: 20, marginBottom: 20,
        borderLeft: '4px solid #10b981', boxShadow: '0 2px 12px rgba(16,185,129,0.1)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, marginBottom: 6, fontWeight: 500 }}>
          <span>Razem netto:</span><span>{calculations.totals.netto.toFixed(2)} {form.currency}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, marginBottom: 8, fontWeight: 500 }}>
          <span>Razem VAT:</span><span>{calculations.totals.vat.toFixed(2)} {form.currency}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 20, fontWeight: 800, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <span>Do zapłaty:</span><span style={{ color: '#10b981' }}>{calculations.totals.brutto.toFixed(2)} {form.currency}</span>
        </div>
      </div>

      {/* Notes */}
      <Section title="📝 Uwagi">
        <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.notes}
          onChange={e => upd('notes', e.target.value)} placeholder="Uwagi do faktury..." />
      </Section>

      {form.invoice_type === 'correction' && (
        <Section title="📝 Przyczyna korekty">
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.correction_reason}
            onChange={e => upd('correction_reason', e.target.value)} placeholder="Powód wystawienia korekty *" />
        </Section>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
        <button className="btn btn-primary" onClick={saveInvoice} disabled={saving}
          style={{ padding: 16, fontSize: 16, fontWeight: 800, borderRadius: 14 }}>
          {saving ? '⏳ Zapisywanie...' : editingId ? '✅ Zapisz zmiany' : `✅ Wystaw ${typeLabels[form.invoice_type]?.split(' ').slice(1).join(' ') || 'dokument'}`}
        </button>
        <button onClick={() => { setTab('list'); setMsg(''); }} style={{
          padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
          background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
        }}>← Powrót do listy</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: '18px 16px', marginBottom: 16,
      border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      <div style={{ fontWeight: 800, marginBottom: 14, fontSize: 16 }}>{title}</div>
      {children}
    </div>
  );
}
